export type ApplicationItemIdSource =
  | string
  | { id?: string | null }
  | null
  | undefined;

export type VaccinationItemIdInput = {
  item_id?: ApplicationItemIdSource;
  next_application_item_id?: ApplicationItemIdSource;
};

export const getVaccinationItemId = (vacc: VaccinationItemIdInput): string | null => {
  const item = vacc.item_id;
  if (typeof item === "string") {
    const trimmed = item.trim();
    return trimmed ? trimmed : null;
  }
  if (item && typeof item === "object" && typeof item.id === "string") {
    const trimmed = item.id.trim();
    return trimmed ? trimmed : null;
  }
  return null;
};

export const getVaccinationNextItemId = (vacc: VaccinationItemIdInput): string | null => {
  const nextItem = vacc.next_application_item_id;
  if (typeof nextItem === "string") {
    const trimmed = nextItem.trim();
    if (trimmed) return trimmed;
  }
  if (nextItem && typeof nextItem === "object" && typeof nextItem.id === "string") {
    const trimmed = nextItem.id.trim();
    if (trimmed) return trimmed;
  }
  return getVaccinationItemId(vacc);
};

type RecencyComparable = {
  id: string;
  application_date?: string | null;
  created_at?: string | null;
};

const safeTime = (iso?: string | null): number => {
  if (!iso) return Number.NEGATIVE_INFINITY;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
};

export const compareVaccinationRecency = (a: RecencyComparable, b: RecencyComparable) => {
  const aApp = safeTime(a.application_date);
  const bApp = safeTime(b.application_date);
  if (aApp !== bApp) return aApp - bApp;

  const aCreated = safeTime(a.created_at);
  const bCreated = safeTime(b.created_at);
  if (aCreated !== bCreated) return aCreated - bCreated;

  return a.id.localeCompare(b.id);
};

export type VaccinationKeyInput = RecencyComparable &
  VaccinationItemIdInput & {
    pet_id?: string | null;
  };

export const filterToLatestVaccinations = <T extends VaccinationKeyInput>(
  candidates: T[],
  allRows: VaccinationKeyInput[],
): T[] => {
  const latestByKey = new Map<string, VaccinationKeyInput>();

  for (const row of allRows) {
    const petId = row.pet_id ?? null;
    const itemId = getVaccinationNextItemId(row);
    if (!petId || !itemId) continue;

    const key = `${petId}::${itemId}`;
    const existing = latestByKey.get(key);
    if (!existing) {
      latestByKey.set(key, row);
      continue;
    }

    if (compareVaccinationRecency(existing, row) < 0) {
      latestByKey.set(key, row);
    }
  }

  return candidates.filter((c) => {
    const petId = c.pet_id ?? null;
    const itemId = getVaccinationNextItemId(c);
    if (!petId || !itemId) return true;
    const key = `${petId}::${itemId}`;
    const latest = latestByKey.get(key);
    return !latest || latest.id === c.id;
  });
};

export type AppointmentLike = {
  id: string;
  type?: string | null;
  starts_at: string;
  status?: string | null;
};

export type AppointmentPickInput = {
  appointment_id?: string | null;
  next_dose_date?: string | null;
  appointments?: AppointmentLike[] | null;
};

export const pickPreferredVaccineAppointment = (
  vacc: AppointmentPickInput,
): AppointmentLike | null => {
  const list = (vacc.appointments ?? []).filter((a) => a && typeof a === "object");
  if (list.length === 0) return null;

  const relevantTypes = new Set(["vacina", "consulta", "retorno", "aplicacao"]);
  const relevantAppts = list.filter((a) => a.type == null || relevantTypes.has(a.type));
  const appts = relevantAppts.length > 0 ? relevantAppts : list;

  if (vacc.appointment_id) {
    const match = appts.find((a) => a.id === vacc.appointment_id);
    if (match) return match;
  }

  const isActiveStatus = (status?: string | null) =>
    status != null && ["agendado", "confirmado", "em_atendimento"].includes(status);
  const active = appts.filter((a) => isActiveStatus(a.status));
  const pool = active.length > 0 ? active : appts;

  const nextDoseTime = vacc.next_dose_date ? new Date(vacc.next_dose_date).getTime() : null;

  const withTimes = pool
    .map((a) => ({ a, t: new Date(a.starts_at).getTime() }))
    .filter((x) => Number.isFinite(x.t));

  if (withTimes.length === 0) return null;

  if (nextDoseTime != null && Number.isFinite(nextDoseTime)) {
    withTimes.sort((x, y) => {
      const dx = Math.abs(x.t - nextDoseTime);
      const dy = Math.abs(y.t - nextDoseTime);
      if (dx !== dy) return dx - dy;
      return x.t - y.t;
    });
    return withTimes[0].a;
  }

  withTimes.sort((x, y) => x.t - y.t);
  return withTimes[0].a;
};

export type AppointmentBadgeKind = "agendado" | "perdido" | "cancelado" | "faltou";

export const getAppointmentBadgeKind = (
  appointment: Pick<AppointmentLike, "status" | "starts_at">,
  now: Date,
): AppointmentBadgeKind => {
  const status = appointment.status ?? null;
  if (status === "cancelado") return "cancelado";
  if (status === "faltou") return "faltou";

  const startsAt = new Date(appointment.starts_at);
  const isPast = startsAt.getTime() < now.getTime();
  const isFinalizedOrPaid = status === "finalizado" || status === "pago";
  if (isPast && !isFinalizedOrPaid) return "perdido";
  return "agendado";
};
